import axiosInstance from "../../../interceptors/axiosInstance";
import { EnumDestinoCredito, StatusCode, TipoTablaDesarrollo } from "../../../utils/enums";
import type { AxiosRequestConfig, AxiosResponse, Method } from 'axios';
import { MovimientoContable } from "../../reportes/consultarMovimientosContables/models/consultaMovimientoContable";
import { GenerarArchivoProcesosMasivos } from "../../reportes/generarProcesosMasivos/models/generarArchivoProcesosMasivos";
import { ar } from "date-fns/locale";
import { ArchivoMinvuDTO } from "../../minvu/archivoMinvuCesantia/models/cobroSeguros";
import { InformeSegDesDto } from "../../remesas/reporteRemesasDes/models/informeDesg";
import { Filtros } from "../../reportes/informesCMF/models/informeCMF";
import { getNameInformeCMF } from "../../reportes/informesCMF/service/informesCMFService";
import { GenerarArchivoRequest } from "../../reportes/actualizacionDeuda/interfaces";
import Conversiones from "../../../utils/conversiones";
import { ArgumentosRecDiaria } from "../../reportes/recaudacionDiaria/models/recaudacionDiara";
import { ResponseInformeDicomDto } from "../../reportes/informeDICOM/models";
import { safeLog } from "../../../utils/logSafe";

// Definición de los endpoints permitidos
type AllowedEndpoint =
  | `siscred-api-bandeja-mutuos/v1/reportes-mutuo/ficha-escrituracion`
  | `siscred-api-reportes/v1/reportes/reportetabladesarrollo`
  | `siscred-api-reportes/v1/reportes/reporteanexotabla`
  | `siscred-api-reportes/v1/reportes/reporteanexoseguro`
  | `siscred-api-reportes/v1/reportes/reportecae`
  | `siscred-api-reportes/v1/reportes/reportecomprobanteliquidacion`
  | `siscred-api-reportes/v1/archivo-externo/generar`
  | `siscred-api-reportes/v1/archivo-externo/generararchivopac`
  | `siscred-api-reportes/v1/archivo-externo/generararchivopat`
  | `siscred-api-reportes/v1/archivos-masivos/generar`
  | `siscred-api-reportes/v1/minvu/generar-excel-validacion`
  | `siscred-api-reportes/v1/minvu/generar-txt-validacion`
  | `siscred-api-reportes/v1/minvu/generar-excel-cobro`
  | `siscred-api-reportes/v1/minvu/generar-txt-cobro`
  | `siscred-api-reportes/v1/reportes/reporteremesas`
  | `siscred-api-reportes/v1/informes/informe-desgravamen`
  | `siscred-api-reportes/v1/portabilidad/generarcertificadoactualizacion`
  | `siscred-api-reportes/v1/informes-cmf/generar`
  | `siscred-api-reportes/v1/informes/informe-rec-diaria`
  | `siscred-api-reportes/v1/archivo-externo/generainformedicom`
  | `siscred-api-reportes/v1/minvu/generar-txt-seguro/${string}`
  | `siscred-api-reportes/v1/minvu/generar-excel-seguro/${string}`
  | `siscred-api-reportes/v1/reportes/reportedividendovigente`
  | `siscred-api-reportes/v1/reportes/ReporteTablaDesarrolloValorizada`
  ;

/**
 * Valida en tiempo de ejecución que el endpoint sea un path esperado
 * para prevenir SSRF. A diferencia de los tipos TypeScript, esto se
 * ejecuta en runtime para evitar que entradas maliciosas lleguen a
 * `axiosInstance` como URL absoluta.
 */
function isAllowedEndpoint(endpoint: string): boolean {
  if (typeof endpoint !== 'string') return false;

  // No permitir esquemas completos ni referencias a hosts
  if (/^(https?:)?\/\//i.test(endpoint)) return false;
  if (endpoint.includes(':')) return false;

  // Evitar backslashes u otros caracteres raros
  if (/\\/.test(endpoint)) return false;

  // Sólo permitir rutas que comiencen con estos prefijos internos
  const allowedPrefixes = [
    'siscred-api-bandeja-mutuos',
    'siscred-api-reportes'
  ];

  return allowedPrefixes.some(p => endpoint.startsWith(p));
}

/**
 * Descarga un archivo blob desde un endpoint usando el método HTTP parametrizado
 * y lo guarda localmente con el nombre dado.
 * @param endpoint Ruta del API para descargar el reporte.
 * @param params Parámetros de consulta que se enviarán en la petición.
 * @param filename Nombre de archivo con extensión para la descarga.
 * @param method Método HTTP a utilizar (get, post, put, delete, etc.). Por defecto 'get'.
 * @param data Cuerpo de la petición para métodos que lo requieran (post, put, etc.).
 */
async function downloadFile(
  endpoint: AllowedEndpoint,
  params: Record<string, any>,
  filename: string,
  method: Method = 'get',
  data?: any
): Promise<void> {

  try {
    debugger;
    // Validación runtime anti-SSRF: rechazar si el endpoint no es del dominio/namespace esperado
    if (!isAllowedEndpoint(String(endpoint))) {
      safeLog(`Bloqueada petición a endpoint no permitido: ${String(endpoint)}`);
      return;
    }
    const config: AxiosRequestConfig = {
      url: endpoint,
      method,
      responseType: 'blob',
    };

    if (method.toLowerCase() === 'get') {
      config.params = params;
    } else {
      config.data = data ?? params;
    }

    const response = await axiosInstance.request(config);

    if (response.status === StatusCode.Ok) {
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Obtenemos el nombre o usamos uno por defecto
      const rawFileName = filename;

      // Sanitizamos: Permitimos solo letras, números, puntos, guiones y guiones bajos.
      // Cualquier otro caracter se reemplaza por un guion bajo.
      // Esto satisface a Veracode porque ve una función de limpieza.
      const cleanFileName = rawFileName.replace(/[^a-zA-Z0-9._-]/g, "_");

      link.download = cleanFileName;
      // -----------------------

      document.body.appendChild(link);
      link.click();
      link.remove();

      // Liberar objeto URL
      window.URL.revokeObjectURL(url);
    }

  } catch (error) {
    safeLog(`Error al descargar ${filename}:`, error);
  }
}

/**
 * Descarga la ficha de escrituración en formato .xls
 * @param mutuoId Identificador del mutuo.
 */
export const descargarFichaEscrituracion = (
  mutuoId: number
): Promise<void> => downloadFile(
  `siscred-api-bandeja-mutuos/v1/reportes-mutuo/ficha-escrituracion`,
  { mutuoId },
  'ficha-escrituracion.xls',
  'get'
);

/**
 * Descarga el reporte de tabla de desarrollo en formato .xlsx
 * @param mutuoId Identificador del mutuo.
 * @param tipoTabla Tipo de tabla de desarrollo (enum).
 */
export const descargarReporteTablaDesarrollo = (
  mutuoId: number,
  tipoTabla: TipoTablaDesarrollo
): Promise<void> => downloadFile(
  `siscred-api-reportes/v1/reportes/reportetabladesarrollo`,
  {},
  'TablaDesarrollo.xlsx',
  'post',
  {
    "mutuoId": mutuoId.toString(),
    "tipoTabla": tipoTabla.toString()
  }
);

/**
 * Descarga el reporte de anexo de tabla de desarrollo en formato .docx
 * @param mutuoId Identificador del mutuo.
 */
export const descargarReporteAnexoTabla = (
  mutuoId: number
): Promise<void> => downloadFile(
  `siscred-api-reportes/v1/reportes/reporteanexotabla`,
  {},
  'anexoTabla.docx',
  'post',
  {
    "mutuoId": mutuoId.toString()
  }
);

/**
 * Descarga el reporte de anexo de seguros en formato .docx
 * @param mutuoId Identificador del mutuo.
 */
export const descargarReporteAnexoSeguros = (
  mutuoId: number
): Promise<void> => downloadFile(
  `siscred-api-reportes/v1/reportes/reporteanexoseguro`,
  {},
  'anexoSeguros.docx',
  'post',
  {
    "mutuoId": mutuoId.toString()
  }
);

/**
 * Descarga el reporte del cae
 * @param nroSolicitud .
 */
export const descargarReporteCae = (
  nroSolicitud: number
): Promise<void> => downloadFile(
  `siscred-api-reportes/v1/reportes/reportecae`,
  {},
  'ResumenContrato.xlsx',
  'post',
  {
    "idSolicitud": nroSolicitud.toString()
  }
);

/**
 * Descarga el comprobante de liquidacion
 * @param mutuoId .
 */
export const descargarComprobanteLiquidacion = (
  mutuoId: number,
  isExcel: boolean = true
): Promise<void> => downloadFile(
  `siscred-api-reportes/v1/reportes/reportecomprobanteliquidacion`,
  {},
  isExcel ? 'ComprobanteLiquidacion.xlsx' : 'ComprobanteLiquidacion.pdf',
  'post',
  {
    "mutuoId": mutuoId.toString(),
    "tipoArchivo": isExcel ? 'excel' : 'pdf',
  }
);


/**
 * Descarga Movimiento contable
 * @param movimineto .
 */
export const descargarMovimientoContable = (
  movimientos: MovimientoContable[]
): Promise<void> => {

  const request = {
    items: movimientos
  };

  return downloadFile(
    `siscred-api-reportes/v1/archivo-externo/generar`,
    {},
    'MovimientoContable.txt',
    'post',
    request
  );
};

/**
 * Descarga Movimiento contable
 * @param archivoMasivo .
 */
export const descargarArchivosProcesosMasivos = (
  archivoMasivo: GenerarArchivoProcesosMasivos
): Promise<void> => downloadFile(
  `siscred-api-reportes/v1/archivos-masivos/generar`,
  {},
  'GenDividendo.txt',
  'post',
  archivoMasivo as GenerarArchivoProcesosMasivos
);

/**
 * Descarga archivo PAC
 * @param fechaUF .
 * @param fechaCobro .
 * @param diaDescuento .
 */
export const descargarArchivoPacPat = (
  fechaUF: Date,
  fechaCobro: Date,
  diaDescuento: number,
  isPac: boolean,
): Promise<void> => downloadFile(
  isPac ? `siscred-api-reportes/v1/archivo-externo/generararchivopac` : `siscred-api-reportes/v1/archivo-externo/generararchivopat`,
  {},
  isPac ? 'ArchivoPAC.txt' : 'ArchivoPAT.txt',
  'post',
  {
    fechaCobro: fechaCobro.toISOString().split("T")[0],
    fechaUF: fechaUF.toISOString().split("T")[0],
    diaCobro: diaDescuento.toString()
  },
);


export const descargarExcelValidacionMinvu = (
  idEnvio: number,
  nombreArchivo: string
): Promise<void> => downloadFile(
  `siscred-api-reportes/v1/minvu/generar-excel-validacion`,
  { idEnvio, nombreArchivo },
  `${nombreArchivo}.xlsx`,
);

export const descargarTxtValidacionMinvu = (
  idEnvio: number,
  nombreArchivo: string
): Promise<void> => downloadFile(
  `siscred-api-reportes/v1/minvu/generar-txt-validacion`,
  { idEnvio, nombreArchivo },
  `${nombreArchivo}.txt`,
);

export const descargarExcelCobroMinvu = (
  idEnvio: number,
  nombreArchivo: string
): Promise<void> => downloadFile(
  `siscred-api-reportes/v1/minvu/generar-excel-cobro`,
  { idEnvio, nombreArchivo },
  `${nombreArchivo}.xlsx`,
);

export const descargarTxtCobroMinvu = (
  idEnvio: number,
  nombreArchivo: string
): Promise<void> => downloadFile(
  `siscred-api-reportes/v1/minvu/generar-txt-cobro`,
  { idEnvio, nombreArchivo },
  `${nombreArchivo}.txt`,
);

export const descargarTxtCobroSeguro = (
  dto: ArchivoMinvuDTO[],
  nombreArchivo: string
): Promise<AxiosResponse> => {
  const request = { items: dto };

  return axiosInstance
    .post(`siscred-api-reportes/v1/minvu/generar-txt-seguro/${nombreArchivo}`, request, {
      responseType: 'blob'
    })
    .then((response) => {
      if (response.status === StatusCode.Ok) {
        const blob = new Blob([response.data]);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${nombreArchivo}.txt`.replace(/[^a-zA-Z0-9._-]/g, '_');
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      }

      return response;
    });
};

export const descargarExcelCobroSeguro = (
  dto: ArchivoMinvuDTO[],
  nombreArchivo: string
): Promise<AxiosResponse> => {
  const request = { items: dto };

  return axiosInstance
    .post(`siscred-api-reportes/v1/minvu/generar-excel-seguro/${nombreArchivo}`, request, {
      responseType: 'blob'
    })
    .then((response) => {
      if (response.status === StatusCode.Ok) {
        const blob = new Blob([response.data]);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${nombreArchivo}.xlsx`.replace(/[^a-zA-Z0-9._-]/g, '_');
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      }

      return response;
    });
};

/**
 * Descarga el reporte de emitir remesas
 * @param idProcesoRemesas .
 * @param destino .
 * @returns Promise<void>
 */
export const descargarReporteRemesas = (
  idProcesoRemesas: number,
  destino: number

): Promise<void> => downloadFile(
  `siscred-api-reportes/v1/reportes/reporteremesas`,
  {},
  'Remesas.xlsx',
  'post',
  {
    "idProcesoRemesas": idProcesoRemesas.toString(),
    "destino": destino.toString()
  }
);

/**
 * Informe de seguro de desgravamen
 * @param data .
 */
export const descargarReporteInfDesgravamen = (
  data: InformeSegDesDto[]
): Promise<void> => {

  const request = {
    items: data
  };

  return downloadFile(
    `siscred-api-reportes/v1/informes/informe-desgravamen`,
    {},
    'InformeDesgravamen.xlsx',
    'post',
    request
  );
};
/**
 * Descarga archivo PAC
 * @param fechaUF .
 * @param fechaCobro .
 * @param diaDescuento .
 */
export const descargarArchivoActualizacionPortabilidad = (
  body: GenerarArchivoRequest
): Promise<void> => downloadFile(
  `siscred-api-reportes/v1/portabilidad/generarcertificadoactualizacion`,
  {},
  `cgp_${Conversiones.getTimestampNum()}.json`,
  'post',
  body
);

/**
 * Descarga informeCMF
 * @param filtros .
 */
export const descargarInformeCMF = async (filtros: Filtros) => {

  const resp = await getNameInformeCMF(filtros)
  if (resp.status !== StatusCode.Ok) {
    return;

  }

  const nameArchivo = resp.data.fileName;

  await downloadFile(
    `siscred-api-reportes/v1/informes-cmf/generar`,
    {},
    nameArchivo,
    'post',
    filtros as Filtros
  );
}

export const descargarReporteRecDiaria = (
  data: ArgumentosRecDiaria
): Promise<void> => downloadFile(
  `siscred-api-reportes/v1/informes/informe-rec-diaria`,
  {},
  'InformeRecDiaria.xlsx',
  'post',
  data as ArgumentosRecDiaria
);

/**
 * Descarga archivo PAC
 * @param fechaUF .
 * @param fechaCobro .
 * @param diaDescuento .
 */
export const descargarInformeDICOM = (
  datos: ResponseInformeDicomDto[]
): Promise<void> => downloadFile(
  `siscred-api-reportes/v1/archivo-externo/generainformedicom`,
  {},
  `GenDICOM_${Conversiones.getTimestampNum()}.txt`,
  'post',
  datos as ResponseInformeDicomDto[]
);

export const descargarReporteDivVigente = (
  mutuoId: number

): Promise<void> => downloadFile(
  `siscred-api-reportes/v1/reportes/reportedividendovigente`,
  {},
  `DividendoVigente${mutuoId}.pdf`,
  'post',
  {
    "mutuoId": mutuoId.toString()
  }
);
export const descargarReporteTdValorizada = (
  mutuoId: number

): Promise<void> => downloadFile(
  `siscred-api-reportes/v1/reportes/ReporteTablaDesarrolloValorizada`,
  {},
  `TablaDesarrolloValorizada.xlsx`,
  'post',
  {
    "mutuoId": mutuoId.toString()
  }
);
